import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { almacen } from '../api';
import { Boton, Cargando, Fallo, Modal, Rotulo } from '../componentes';

/**
 * «Asignar serie»: dice qué equipo se llevó una venta que salió sin él.
 *
 * Antes un aviso de serie solo se cerraba con «Revisado», y el equipo seguía
 * «en el estante» con su lote en uno: la caja lo volvía a ofrecer y el conteo
 * no cuadraba nunca (8 oct, revisión del almacén de tecnología). Aquí se elige
 * la venta —si hay más de una sin equipo— y el equipo de la vitrina que fue:
 * la salida pasa a ser de ese lote y el equipo queda vendido con su garantía.
 * Cuando ya no queda ninguna venta sin equipo, el aviso se cierra solo.
 */
export function AsignarSerie({
  alertId,
  producto,
  onCerrar,
}: {
  alertId: string;
  producto: string;
  onCerrar: () => void;
}) {
  const clientes = useQueryClient();
  const opciones = useQuery({
    queryKey: ['asignar-serie', alertId],
    queryFn: () => almacen.serialAlertAssignment(alertId),
  });
  const [venta, setVenta] = useState<string>('');
  const [serie, setSerie] = useState<string>('');

  const ventas = opciones.data?.sales ?? [];
  const equipos = opciones.data?.units ?? [];
  const ventaElegida = venta !== '' ? venta : (ventas[0]?.movementId ?? '');

  const asignar = useMutation({
    mutationFn: () =>
      almacen.assignSerialToAlert(alertId, { movementId: ventaElegida, serialNumber: serie }),
    onSuccess: () => {
      void clientes.invalidateQueries();
      onCerrar();
    },
  });

  return (
    <Modal titulo={`Asignar serie · ${producto}`} onCerrar={onCerrar}>
      {opciones.isPending ? (
        <Cargando que="las ventas sin equipo" />
      ) : opciones.isError ? (
        <Fallo error={opciones.error} onReintentar={() => opciones.refetch()} />
      ) : (
        <div className="formulario">
          {ventas.length === 0 ? (
            <p className="aviso-caja">
              Ya no queda ninguna venta sin equipo de este producto: puedes cerrar el aviso con
              «Revisado».
            </p>
          ) : (
            <>
              <p className="aviso-caja">
                Elige qué equipo se llevó el cliente. El estante ya bajó al vender: aquí solo se
                dice cuál fue, y deja de ofrecerse en la caja.
              </p>
              {ventas.length > 1 ? (
                <div className="campo">
                  <Rotulo
                    htmlFor="venta-sin-equipo"
                    ayuda="Las ventas de este producto que salieron sin decir qué equipo era, la más vieja primero."
                  >
                    Venta
                  </Rotulo>
                  <select
                    id="venta-sin-equipo"
                    value={ventaElegida}
                    onChange={(e) => setVenta(e.target.value)}
                  >
                    {ventas.map((v) => (
                      <option key={v.movementId} value={v.movementId}>
                        {new Date(v.occurredAt).toLocaleString('es-PE', { timeZone: 'America/Lima' })}
                        {v.serialSaid ? ` · dijo ${v.serialSaid}` : ' · sin serie'}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="campo">
                <Rotulo
                  htmlFor="equipo-de-la-venta"
                  ayuda="Los equipos de este producto que el almacén cree en el estante. El que se llevó el cliente ya no está en la vitrina."
                >
                  Equipo
                </Rotulo>
                <select
                  id="equipo-de-la-venta"
                  value={serie}
                  onChange={(e) => setSerie(e.target.value)}
                >
                  <option value="">Elige el equipo</option>
                  {equipos.map((u) => (
                    <option key={u.serialNumber} value={u.serialNumber}>
                      {u.serialNumber} · {u.conditionLabel}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {asignar.isError ? <Fallo error={asignar.error} /> : null}
          <div className="formulario__fila">
            <Boton onClick={onCerrar}>Cancelar</Boton>
            {ventas.length > 0 ? (
              <Boton
                variante="primario"
                onClick={() => asignar.mutate()}
                disabled={asignar.isPending || serie === ''}
              >
                {asignar.isPending ? 'Guardando…' : 'Asignar'}
              </Boton>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}
